/**
 * GA4 wiring (G-MHS56N4N45). The gtag script loads only in production builds — in dev,
 * events log to the console instead of polluting the property. The SPA owns page_view
 * (send_page_view:false + manual fires on hashchange), so hash routes (#gallery, #privacy…)
 * show up as distinct pages.
 *
 * Funnel vocabulary (GA4 ecommerce where it exists, custom elsewhere):
 *   connect_audio {method,surface}      — activation: a real audio source connected
 *   select_world {world_id,surface}     — engagement: a world chosen (picker / gallery)
 *   director_toggle / fullscreen / support_open / manage_subscription
 *   subscribe_modal_view {reason}       — the paywall (gate) or support ask appeared
 *   begin_checkout {value,items}        — a Stripe payment link was clicked
 *   purchase {transaction_id,value}     — /api/activate confirmed the checkout (redirect params)
 *   claim_* — the existing-subscriber email claim funnel
 */
import { PRICE_USD } from './pricing'

const GA_ID = 'G-MHS56N4N45'

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

const enabled = import.meta.env.PROD

export function initAnalytics(): void {
  if (!enabled) return
  window.dataLayer = window.dataLayer ?? []
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments)
  }
  window.gtag('js', new Date())
  // the SPA sends page_view itself (hash routing is invisible to the default snapshot)
  window.gtag('config', GA_ID, { send_page_view: false })
  const s = document.createElement('script')
  s.async = true
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`
  document.head.appendChild(s)

  pageView()
  window.addEventListener('hashchange', pageView)
}

function pageView(): void {
  track('page_view', {
    page_location: window.location.href,
    page_title: pageTitle(),
    page_path: window.location.pathname + (window.location.hash || '')
  })
}

function pageTitle(): string {
  const h = window.location.hash
  if (h === '#gallery') return 'SYQEL Art — World Gallery'
  if (h === '#privacy') return 'SYQEL Art — Privacy'
  if (h === '#terms') return 'SYQEL Art — Terms'
  if (h === '#support') return 'SYQEL Art — Support'
  return document.title
}

/** Where the user currently is — attached to events whose meaning depends on the view. */
export function surface(): string {
  return window.location.hash === '#gallery' ? 'gallery' : 'player'
}

export function track(name: string, params?: Record<string, unknown>): void {
  if (!enabled) {
    console.debug('[syqel·ga]', name, params ?? {})
    return
  }
  window.gtag?.('event', name, params)
}

export interface Plan {
  id: 'syqel-art-annual' | 'syqel-art-monthly'
  name: string
  price: number
}

export const PLANS: Record<'annual' | 'monthly', Plan> = {
  annual: { id: 'syqel-art-annual', name: 'SYQEL Art — Annual', price: PRICE_USD.annual },
  monthly: { id: 'syqel-art-monthly', name: 'SYQEL Art — Monthly', price: PRICE_USD.monthly }
}

export function trackBeginCheckout(plan: Plan): void {
  track('begin_checkout', {
    currency: 'USD',
    value: plan.price,
    items: [{ item_id: plan.id, item_name: plan.name, price: plan.price, quantity: 1 }]
  })
}

/**
 * /api/activate appends value (cents) + transaction id to its /?sub=ok redirect so the client
 * can report the purchase GA4 never witnessed (checkout happens on pay.syqel.com).
 */
export function trackPurchase(valueCents: number, transactionId: string): void {
  const value = valueCents / 100
  const plan = valueCents >= 3000 ? PLANS.annual : PLANS.monthly
  track('purchase', {
    transaction_id: transactionId,
    currency: 'USD',
    value,
    items: [{ item_id: plan.id, item_name: plan.name, price: value, quantity: 1 }]
  })
}
